/**
 * @file src/offline/offlineLibraryStore.js
 * @description What has been downloaded, and everything needed to play it with no server.
 *
 * The bytes are not here. They live in the audio bucket, pinned (`audioCacheStore.js`), because a
 * downloaded track and a recently-played one are the same bytes under the same name and storing
 * them twice would be both wasteful and a second thing to keep in step. What lives here is
 * everything that is NOT bytes:
 *
 *   `collections`  one manifest per downloaded collection PER OWNER — the roster, version-pinned,
 *                  and the logical key of every asset it needs, with the URL each was resolved
 *                  from. Keyed by owner and collection together, so two people sharing a browser
 *                  each hold their own copy of the same collection, with their own pins.
 *   `releases`     the hydrated release payloads, so an offline boot has the session to build from
 *                  rather than a request it cannot make.
 *   `meta`         the last established owner uid, and a note of which one-shot repairs have run.
 *   `configurations` the user's own configuration, mirrored from the server so it survives a boot
 *                  with no network. Keyed by OWNER, kind and resource together — see the section
 *                  at the bottom of this file for why that compound key is the whole point.
 *
 * ## Two rules this store does not get to bend
 *
 * **It never answers "is this available offline" on its own.** A manifest can outlive its bytes —
 * a browser may reclaim an origin's storage whole, and nothing tells the page it happened. So a
 * manifest is a statement of intent and the bucket is the statement of fact; the reader
 * (`offlineLibrary.js`) checks the bucket before showing anything as ready.
 *
 * **A download belongs to whoever downloaded it, and nothing takes it away.** Not signing out, not
 * somebody else signing in. A change of identity changes which manifests are VISIBLE — every read
 * here is filtered to the owner the meta row names — and removes nothing. This is a person's own
 * storage: managing it is theirs, and a browser two people share holds both libraries side by side.
 *
 * The bytes underneath are shared all the same (one bucket, refcounted pins), so two people holding
 * the same track hold one copy of it and two pins. Which means the isolation here is of the
 * LIBRARY, not of the disk: the other person's files are still on it, out of sight rather than out
 * of reach. Real isolation between people on one machine is a separate browser profile.
 *
 * ## Whose library is shown when nobody could be asked
 *
 * The meta row is what an offline boot reads. It holds the last uid the server actually confirmed,
 * so a boot with no network — where identity cannot be established at all — shows the library of
 * the person who was last really here, rather than nothing.
 *
 * ## Versions are pinned, never mutated
 *
 * An entry names the exact `trackVersion` / `orbiterVersion` / `entangledWorldVersion` it was
 * downloaded at, and those versions are what the offline boot asks for. A newer release seen online
 * is a NEW key and a separate fact — "update available" — never a silent replacement: bytes someone
 * asked to keep are not swapped underneath them for bytes they did not.
 */

import {
  OFFLINE_COLLECTIONS_STORE,
  OFFLINE_LIBRARY_DB_NAME,
} from './offlineAssetIndex.js';

// Declared in `offlineAssetIndex.js` rather than here, because the service worker reads these
// manifests too and must not import this module to learn where they live. One declaration, two
// readers.
const DB_NAME = OFFLINE_LIBRARY_DB_NAME;
/**
 * v2 re-keyed `collections` by owner AND collection, so one browser can hold two libraries.
 * v3 added `configurations` — the same move for the settings a release used to carry inside it.
 */
const DB_VERSION = 3;
const COLLECTIONS_STORE = OFFLINE_COLLECTIONS_STORE;
const RELEASES_STORE = 'releases';
const META_STORE = 'meta';
const CONFIGURATIONS_STORE = 'configurations';
const OWNER_ROW_KEY = 'owner';
const COLLECTION_KEY_PATH = 'recordId';
/** The compound key every configuration row is filed under. Owner FIRST — see the section below. */
const CONFIGURATION_KEY_PATH = ['ownerKey', 'kind', 'resourceKey'];

/**
 * Who a manifest belongs to, as one string.
 *
 * Three cases, and the third is the reason this is not simply the uid. `anon` is a real owner — a
 * signed-out visitor's downloads are still theirs, and the next signed-out visitor is the same
 * "who" as far as this browser can tell. `unowned` is a manifest stored before downloads had
 * owners at all: there is no evidence about whose it is, so it is shown to whoever opens the
 * library until a reconcile with a known owner stamps it (see {@link stampUnownedCollections}).
 */
const OWNER_UNOWNED = 'unowned';
const OWNER_ANONYMOUS = 'anon';

function ownerKeyForUid(uid) {
  return uid ? `uid:${uid}` : OWNER_ANONYMOUS;
}

/** A stored manifest's owner. The FIELD's absence is what marks a pre-owner download. */
function ownerKeyOfManifest(manifest) {
  return manifest && manifest.ownerUid !== undefined
    ? ownerKeyForUid(manifest.ownerUid)
    : OWNER_UNOWNED;
}

function collectionRecordId(ownerKey, collectionId) {
  return `${ownerKey}|${collectionId}`;
}

/** What a guarded write settles its transaction with when the owner moved — never a stored row. */
const OWNER_MOVED = Symbol('owner-moved');

/**
 * Raised when work outlives the identity it was started for — by the owner guards below, and by the
 * download runs in `offlineLibrary.js` that carry the same question.
 *
 * It lives here because the guards are here, and there is one of it because there is one situation:
 * whatever this was, it was for somebody who is no longer the person the library belongs to. A run
 * that catches it hands back the pins it took; nothing else surfaces it.
 */
export class StaleIdentityError extends Error {
  constructor(message = 'The viewer changed while this work was running.') {
    super(message);
    this.name = 'StaleIdentityError';
  }
}

/** A collection's lifecycle. `partial` is a download that stopped — resumable, not broken. */
export const DOWNLOAD_STATE = {
  DOWNLOADING: 'downloading',
  READY: 'ready',
  PARTIAL: 'partial',
};

/**
 * How long an open may take before it is treated as a refusal.
 *
 * An IndexedDB open does not always answer. A `deleteDatabase` still blocked by a connection in
 * another tab leaves later opens queued behind it, firing neither success nor error nor blocked —
 * and this store is opened during boot, so a queue like that took the whole app down with a black
 * screen. Nothing here is worth that: an open that will not answer means downloads are unavailable
 * for this session, which is a feature quietly missing rather than an app that never starts.
 */
const OPEN_TIMEOUT_MS = 3000;

let dbPromise = null;

/**
 * Resolves once the owner has been reconciled — i.e. once it is known that whatever is in this
 * store belongs to the person now here.
 *
 * Boot does not await the reconcile (it is an IndexedDB open on the critical path to the app
 * existing), so without this a drawer could open, read the store against a stale owner row and
 * briefly show the PREVIOUS person's collection names. Reads wait on this instead; the rest of the
 * app does not.
 */
let ownerReady = Promise.resolve();

/**
 * One resolver per armed gate, oldest first — NOT a single mutable one.
 *
 * Two reconciles can be armed before either finishes: an embed swapping host tokens does exactly
 * that. With one resolver the second arming overwrites the first, so the FIRST reconcile to finish
 * opens the SECOND's gate — and a read in that window is answered against the owner row the second
 * reconcile has not written yet, which is one person's collection names shown to another. Handing
 * each finish the oldest outstanding resolver keeps the newest gate shut until as many reconciles
 * have finished as were armed.
 */
const pendingOwnerGates = [];

/**
 * Declare that an owner reconcile is about to run. Reads after this point wait for it.
 * A second call before the first settles arms a second gate; both are released in turn.
 */
export function expectOwnerReconcile() {
  ownerReady = new Promise((resolve) => {
    pendingOwnerGates.push(resolve);
  });
}

/** Release the oldest gate still waiting. A finish with none armed has nothing to open. */
function markOwnerReady() {
  pendingOwnerGates.shift()?.();
}

/**
 * Everything that reads per-user state waits here first — but never indefinitely.
 *
 * Exported, because the collections and releases in this file are not the only per-user state on
 * the page: a MIDI mapping read is answered for whoever the owner row names, and one issued before
 * the reconcile is answered for whoever was here last.
 *
 * The gate exists so a read cannot see the previous owner's collections; it must not become a way
 * for the whole surface to disappear. A reconcile that never settles (its store did not open, and
 * the open itself did not answer) would otherwise leave every read pending forever, which shows up
 * as a download panel that renders no state at all and explains nothing. Waiting out the store's
 * own open timeout and then proceeding is the honest ceiling: by then the reconcile has either
 * happened or cannot.
 */
export function whenOwnerReady() {
  return Promise.race([
    ownerReady,
    new Promise((resolve) => setTimeout(resolve, OPEN_TIMEOUT_MS + 500)),
  ]);
}

/** One queue for every mutation, for the same reason the audio ledger has one: two writes planned
 *  from the same pre-write state and applied afterwards lose one of them. */
let queue = Promise.resolve();

function serialize(work) {
  const settled = queue.then(work, work);
  queue = settled.then(
    () => undefined,
    () => undefined,
  );
  return settled;
}

function getIndexedDbFactory() {
  return typeof globalThis !== 'undefined' && globalThis.indexedDB ? globalThis.indexedDB : null;
}

/** The stores this version must have. A database missing any of them is broken, not merely empty. */
const REQUIRED_STORES = [COLLECTIONS_STORE, RELEASES_STORE, META_STORE, CONFIGURATIONS_STORE];

/**
 * Build the stores, and carry a v1 `collections` store across to the composite key.
 *
 * v1 keyed a manifest by `collectionId` alone, which is one row per collection for the whole
 * browser — so the second person to download the same collection would have overwritten the
 * first's copy, pins and all. The key is now owner-and-collection, and the rows already here are
 * re-keyed as `unowned` rather than guessed at: whose they are is written down at the next
 * reconcile, by the owner row that was standing for them all along.
 *
 * Everything happens inside the versionchange transaction — a request chained off `getAll` keeps
 * it alive, and abandoning it halfway is what leaves a database permanently half-made.
 */
function createStores(db, tx) {
  if (!db.objectStoreNames.contains(RELEASES_STORE)) {
    db.createObjectStore(RELEASES_STORE, { keyPath: 'key' });
  }
  if (!db.objectStoreNames.contains(META_STORE)) {
    db.createObjectStore(META_STORE, { keyPath: 'key' });
  }
  // v3. Additive — nothing to carry across, because nothing was here before. What is personal in
  // the effective settings the OLD rows in `releases` are still carrying is taken out by
  // `stripStoredEffectiveSettings`, which runs after the upgrade rather than inside it: a repair
  // that fails must leave a usable database, and an abandoned versionchange transaction leaves an
  // unusable one forever.
  if (!db.objectStoreNames.contains(CONFIGURATIONS_STORE)) {
    db.createObjectStore(CONFIGURATIONS_STORE, { keyPath: CONFIGURATION_KEY_PATH });
  }
  if (!db.objectStoreNames.contains(COLLECTIONS_STORE)) {
    db.createObjectStore(COLLECTIONS_STORE, { keyPath: COLLECTION_KEY_PATH });
    return;
  }

  const existing = tx?.objectStore(COLLECTIONS_STORE);
  if (!existing || existing.keyPath === COLLECTION_KEY_PATH) return;

  const all = existing.getAll();
  all.onsuccess = () => {
    const rows = all.result || [];
    db.deleteObjectStore(COLLECTIONS_STORE);
    const rebuilt = db.createObjectStore(COLLECTIONS_STORE, { keyPath: COLLECTION_KEY_PATH });
    for (const row of rows) {
      if (!row?.collectionId) continue;
      rebuilt.put({ ...row, [COLLECTION_KEY_PATH]: collectionRecordId(ownerKeyOfManifest(row), row.collectionId) });
    }
  };
}

function hasEveryStore(db) {
  return REQUIRED_STORES.every((name) => db.objectStoreNames.contains(name));
}

/**
 * Whether this database is one this version can actually read.
 *
 * Having the stores is not enough: `collections` must also be keyed the way every read keys it. A
 * database carrying the CURRENT version number with the PREVIOUS key is unreadable and unfixable
 * on its own — every lookup misses, and the upgrade that would re-key it never runs again because
 * the version it wants is already there. That state is reachable whenever a version bump and the
 * migration behind it are seen separately (a half-built dev bundle is the obvious way), and it
 * presents as the worst possible symptom: downloads that store their bytes and then vanish.
 *
 * So it is checked rather than assumed, and a database that fails is rebuilt by the caller. Nothing
 * readable is lost, because nothing in it was reachable.
 */
function isUsableLibrary(db) {
  if (!hasEveryStore(db)) return false;
  try {
    return db.transaction(COLLECTIONS_STORE, 'readonly').objectStore(COLLECTIONS_STORE).keyPath
      === COLLECTION_KEY_PATH;
  } catch {
    return false;
  }
}

/** Read a whole store, or nothing at all — a store that will not read is simply not carried. */
function readAllFrom(db, storeName) {
  if (!db.objectStoreNames.contains(storeName)) return Promise.resolve([]);
  return new Promise((resolve) => {
    try {
      const request = db.transaction(storeName, 'readonly').objectStore(storeName).getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

/**
 * Everything worth carrying out of a database that cannot be read as it stands.
 *
 * `getAll` does not need the key to be right — only a lookup does — so the rows are all still here
 * to be taken. Which is the difference between a repair and a loss: a manifest carries the record
 * of which bytes it pinned, and losing it strands those pins raised forever.
 */
async function salvageLibrary(db) {
  try {
    return {
      collections: await readAllFrom(db, COLLECTIONS_STORE),
      releases: await readAllFrom(db, RELEASES_STORE),
      meta: await readAllFrom(db, META_STORE),
      configurations: await readAllFrom(db, CONFIGURATIONS_STORE),
    };
  } catch {
    return { collections: [], releases: [], meta: [], configurations: [] };
  }
}

/** Put the salvaged rows back, each collection under the key this version reads it by. */
async function restoreSalvaged(db, salvaged) {
  const collections = (salvaged?.collections || []).filter((row) => row?.collectionId);
  const releases = (salvaged?.releases || []).filter((row) => row?.key);
  const meta = (salvaged?.meta || []).filter((row) => row?.key);
  // A configuration row without its owner is not a row that can be put back — the owner is half its
  // key, and guessing one would file somebody's settings under somebody else. Dropped instead: this
  // store is a MIRROR, so what it loses costs one request, not a download.
  const configurations = (salvaged?.configurations || []).filter(
    (row) => row?.ownerKey && row?.kind && row?.resourceKey,
  );
  if (!collections.length && !releases.length && !meta.length && !configurations.length) return;

  try {
    await runTransaction(
      db,
      [COLLECTIONS_STORE, RELEASES_STORE, META_STORE, CONFIGURATIONS_STORE],
      'readwrite',
      (stores, done) => {
        for (const row of collections) {
          stores[COLLECTIONS_STORE].put({
            ...row,
            [COLLECTION_KEY_PATH]: collectionRecordId(ownerKeyOfManifest(row), row.collectionId),
          });
        }
        for (const row of releases) stores[RELEASES_STORE].put(row);
        for (const row of meta) stores[META_STORE].put(row);
        for (const row of configurations) stores[CONFIGURATIONS_STORE].put(row);
        done(undefined);
      },
    );
  } catch (error) {
    // A rebuilt-but-empty library still works; it is the pins that mattered, and a failure to put
    // them back is worth saying out loud rather than swallowing.
    console.warn('[offline] The rebuilt library could not take back what was salvaged.', error);
  }
}

function deleteDatabase() {
  return new Promise((resolve) => {
    let request;
    try {
      request = getIndexedDbFactory()?.deleteDatabase(DB_NAME);
    } catch {
      resolve(false);
      return;
    }
    if (!request) {
      resolve(false);
      return;
    }
    const timer = setTimeout(() => resolve(false), OPEN_TIMEOUT_MS);
    const settle = (value) => {
      clearTimeout(timer);
      resolve(value);
    };
    request.onsuccess = () => settle(true);
    request.onerror = () => settle(false);
    request.onblocked = () => settle(false);
  });
}

/**
 * One open attempt, bounded — except while an upgrade is running.
 *
 * The exception is the whole point. Abandoning an open that is midway through `onupgradeneeded`
 * leaves the database CREATED at its new version with its object stores half-made; every later
 * open then sees the version it wants, never fires the upgrade again, and every transaction fails
 * with "object store not found" for good. A timeout that can permanently break a store is worse
 * than the stall it was added to survive, so once an upgrade starts it is allowed to finish.
 * @returns {Promise<IDBDatabase|null>}
 */
function attemptOpen() {
  const factory = getIndexedDbFactory();
  if (!factory) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    let upgrading = false;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = setTimeout(() => {
      if (upgrading) return; // never abandon an upgrade — see above
      settle(null);
    }, OPEN_TIMEOUT_MS);
    const finish = (value) => {
      clearTimeout(timer);
      settle(value);
    };

    let request;
    try {
      request = factory.open(DB_NAME, DB_VERSION);
    } catch {
      finish(null);
      return;
    }

    request.onupgradeneeded = () => {
      upgrading = true;
      createStores(request.result, request.transaction);
    };
    request.onsuccess = () => {
      // A timed-out open can still succeed later. Its connection would then be live and untracked —
      // and an untracked connection is exactly what blocks the next upgrade or delete.
      if (settled) {
        try {
          request.result.close();
        } catch {
          // Nothing more to do; it was never handed out.
        }
        return;
      }
      // Step aside for a later version rather than blocking it.
      //
      // A connection held open across a version bump is what makes the NEXT tab's upgrade fire
      // `blocked` — and a blocked open here reads as "downloads are unavailable", for as long as
      // this tab stays open. Two tabs of the same app, one of them older, is an ordinary way to
      // use a browser, so the older one gives way: it closes, its reads fail over to a fresh open
      // afterwards, and the upgrade proceeds.
      request.result.onversionchange = () => {
        try {
          request.result.close();
        } catch {
          // Already gone; the next open reopens either way.
        }
        dbPromise = null;
      };
      finish(request.result);
    };
    request.onerror = () => finish(null);
    request.onblocked = () => finish(null);
  });
}

/**
 * Opens the library, or resolves null where IndexedDB is unavailable, refuses (a private window,
 * denied storage) or does not answer. Only a successful open is remembered — a transient refusal
 * must not disable downloads for the rest of the page.
 *
 * A database that opens but is not one this version can READ — missing a store, or carrying the
 * current version number with the previous key — is repaired rather than used: what can still be
 * read out of it is carried across, and the database is deleted and rebuilt around it. Without the
 * repair such a database is broken forever, because its version is already current and the upgrade
 * that would fix it never runs again.
 *
 * Carrying the rows is the part that matters, because a manifest is not only a list of names: it
 * is the only record of which bytes it PINNED. Dropping it leaves those pins raised with nothing
 * left to release them — bytes that can never be evicted and never be freed, in a store whose whole
 * job is to stay bounded. The rows are unreadable by KEY, not unreadable, so they are read out
 * whole and put back under the key this version uses.
 * @returns {Promise<IDBDatabase|null>}
 */
function openLibrary() {
  const factory = getIndexedDbFactory();
  if (!factory) return Promise.resolve(null);
  if (dbPromise) return dbPromise;

  const pending = (async () => {
    const db = await attemptOpen();
    if (!db) return null;
    if (isUsableLibrary(db)) return db;

    console.warn('[offline] The offline library could not be read as it stands — rebuilding it.');
    const salvaged = await salvageLibrary(db);
    try {
      db.close();
    } catch {
      // Closing is best-effort; the delete below reports whether it worked.
    }
    if (!(await deleteDatabase())) return null;
    const rebuilt = await attemptOpen();
    if (rebuilt && isUsableLibrary(rebuilt)) await restoreSalvaged(rebuilt, salvaged);
    return rebuilt && isUsableLibrary(rebuilt) ? rebuilt : null;
  })();

  dbPromise = pending.then((db) => {
    if (!db) dbPromise = null;
    return db;
  });

  return dbPromise;
}

/**
 * Runs `work` inside one transaction and settles on the TRANSACTION, so an awaiting caller knows
 * the writes are durable. `work` must chain IDB requests by callback and never await outside them:
 * a transaction that yields to the event loop is closed by the time the next request is issued.
 * @param {IDBDatabase} db
 * @param {string|string[]} storeNames
 * @param {IDBTransactionMode} mode
 * @param {(stores: Record<string, IDBObjectStore>, done: (value: any) => void) => void} work
 */
function runTransaction(db, storeNames, mode, work) {
  const names = Array.isArray(storeNames) ? storeNames : [storeNames];
  return new Promise((resolve, reject) => {
    let tx;
    try {
      tx = db.transaction(names, mode);
    } catch (error) {
      reject(error);
      return;
    }

    let outcome;
    tx.oncomplete = () => resolve(outcome);
    tx.onerror = () => reject(tx.error || new Error('Offline library transaction failed.'));
    tx.onabort = () => reject(tx.error || new Error('Offline library transaction aborted.'));

    try {
      const stores = {};
      for (const name of names) stores[name] = tx.objectStore(name);
      work(stores, (value) => {
        outcome = value;
      });
    } catch (error) {
      try {
        tx.abort();
      } catch {
        // Already going down; the abort handler settles the promise.
      }
      reject(error);
    }
  });
}

// ── Collections ──────────────────────────────────────────────────────────────────────────────────

/** The owner every read below is answered for: whoever the meta row last named. */
function withOwnerKey(metaStore, next) {
  const request = metaStore.get(OWNER_ROW_KEY);
  request.onsuccess = () => next(ownerKeyForUid(request.result?.uid ?? null));
}

/**
 * One collection as this owner sees it: their own manifest, or an unowned one standing in for it.
 *
 * The unowned fallback is the migration case and only that. A manifest stored before downloads had
 * owners is shown to whoever opens the library, because there is no evidence to hide it from
 * anyone — and the first reconcile that knows an owner stamps it, after which this never fires.
 */
function readVisibleCollection(store, ownerKey, collectionId, done) {
  const mine = store.get(collectionRecordId(ownerKey, collectionId));
  mine.onsuccess = () => {
    if (mine.result) {
      done(mine.result);
      return;
    }
    const unowned = store.get(collectionRecordId(OWNER_UNOWNED, collectionId));
    unowned.onsuccess = () => done(unowned.result || null);
  };
}

/** The manifests this owner may see, one per collection — their own copy wins over an unowned one. */
function visibleToOwner(ownerKey, rows) {
  const mine = new Map();
  const unowned = new Map();
  for (const row of rows || []) {
    if (!row?.collectionId) continue;
    const key = ownerKeyOfManifest(row);
    if (key === ownerKey) mine.set(row.collectionId, row);
    else if (key === OWNER_UNOWNED) unowned.set(row.collectionId, row);
  }
  for (const [collectionId, row] of unowned) {
    if (!mine.has(collectionId)) mine.set(collectionId, row);
  }
  return [...mine.values()];
}

/**
 * This owner's downloaded collections, newest first.
 *
 * Somebody else's downloads are not listed, not counted against the limits, and not offered for
 * removal — but they are still here, and their bytes are still pinned. This is a filter, never a
 * deletion.
 * @returns {Promise<object[]>}
 */
export async function listDownloadedCollections() {
  await whenOwnerReady();
  const db = await openLibrary();
  if (!db) return [];
  const rows = await runTransaction(db, [COLLECTIONS_STORE, META_STORE], 'readonly', (stores, done) => {
    withOwnerKey(stores[META_STORE], (ownerKey) => {
      const request = stores[COLLECTIONS_STORE].getAll();
      request.onsuccess = () => done(visibleToOwner(ownerKey, request.result));
    });
  });
  return rows.sort((a, b) => (Number(b?.downloadedAt) || 0) - (Number(a?.downloadedAt) || 0));
}

/** One collection's manifest as this owner sees it, or null. */
export async function readDownloadedCollection(collectionId) {
  if (!collectionId) return null;
  await whenOwnerReady();
  const db = await openLibrary();
  if (!db) return null;
  return runTransaction(db, [COLLECTIONS_STORE, META_STORE], 'readonly', (stores, done) => {
    withOwnerKey(stores[META_STORE], (ownerKey) => {
      readVisibleCollection(stores[COLLECTIONS_STORE], ownerKey, collectionId, done);
    });
  });
}

/**
 * Write (or replace) a collection's manifest, owned by whoever is here.
 *
 * The owner is stamped from the meta row rather than passed in, so there is one answer to "whose
 * download is this" and it is the same answer the reads filter on.
 *
 * `expectedOwnerKey` is how a caller says who it STARTED for — see {@link writeUserConfigurations},
 * which learned this first. A download resolves its manifest over many seconds of requests, and the
 * person here can change while they are in flight; the write then waits for the new reconcile and
 * stamps the ARRIVING person on the LEAVING person's download. Checking the epoch afterwards does
 * not help, because by then the row has committed. So the owner is compared inside the same
 * transaction that writes, and a manifest for the wrong owner is dropped rather than misfiled.
 * @param {object} manifest as built by `collectionManifest.js`
 * @param {{expectedOwnerKey?: string|null}} [options]
 * @returns {Promise<boolean>} whether the manifest was actually stored
 */
export async function writeDownloadedCollection(manifest, { expectedOwnerKey = null } = {}) {
  const collectionId = manifest?.collectionId;
  if (!collectionId) return false;

  await whenOwnerReady();
  return serialize(async () => {
    const db = await openLibrary();
    if (!db) return false;
    return runTransaction(db, [COLLECTIONS_STORE, META_STORE], 'readwrite', (stores, done) => {
      const request = stores[META_STORE].get(OWNER_ROW_KEY);
      request.onsuccess = () => {
        const ownerUid = request.result?.uid ?? null;
        const ownerKey = ownerKeyForUid(ownerUid);
        if (expectedOwnerKey !== null && ownerKey !== expectedOwnerKey) {
          done(false);
          return;
        }
        // Downloading a collection that has been sitting here unowned ADOPTS it rather than
        // leaving a second copy of the same collection that nobody claims.
        stores[COLLECTIONS_STORE].delete(collectionRecordId(OWNER_UNOWNED, collectionId));
        stores[COLLECTIONS_STORE].put({
          ...manifest,
          ownerUid,
          [COLLECTION_KEY_PATH]: collectionRecordId(ownerKey, collectionId),
          updatedAt: Date.now(),
        });
        done(true);
      };
    });
  });
}

/**
 * Merge fields into this owner's manifest — progress, state, an "update available" note.
 * A read-modify-write inside ONE transaction, so a progress tick and a state change landing
 * together cannot lose each other.
 *
 * `expectedOwnerKey` carries who the caller started for, exactly as
 * {@link writeDownloadedCollection} does. A run that settles after somebody else has arrived would
 * otherwise merge its byte count and — worse — its pin record into the arriving person's manifest
 * for the same collection, handing them pins they never took and which their next removal would
 * release out from under the person who did.
 *
 * A dropped write THROWS rather than answering null, and that is the one thing a caller here cannot
 * do without. This is the write that records a run's pins, and null already means "there was no
 * manifest to merge into" — so a run whose pin record was refused read back as a run that had
 * simply finished, resolved as settled, and left every pin it had raised with nothing naming it.
 * Bytes pinned and unrecorded are bytes nothing can ever release. The two silences are told apart.
 * @param {{expectedOwnerKey?: string|null}} [options]
 * @returns {Promise<object|null>} the merged manifest, or null when there was nothing to merge into
 * @throws {StaleIdentityError} when the library now belongs to somebody else
 */
export async function patchDownloadedCollection(collectionId, patch, { expectedOwnerKey = null } = {}) {
  if (!collectionId || !patch) return null;

  await whenOwnerReady();
  return serialize(async () => {
    const db = await openLibrary();
    if (!db) return null;
    // Settled with, rather than thrown from, the transaction callback: the guard runs inside a
    // request's `onsuccess`, and an exception raised there escapes the callback's own try and takes
    // the transaction down as an abort — an error about the wrong thing entirely.
    const merged = await runTransaction(db, [COLLECTIONS_STORE, META_STORE], 'readwrite', (stores, done) => {
      const store = stores[COLLECTIONS_STORE];
      withOwnerKey(stores[META_STORE], (ownerKey) => {
        if (expectedOwnerKey !== null && ownerKey !== expectedOwnerKey) {
          done(OWNER_MOVED);
          return;
        }
        readVisibleCollection(store, ownerKey, collectionId, (existing) => {
          if (!existing) {
            done(null);
            return;
          }
          // Merged onto the row's OWN key, so a patch never moves a manifest between owners.
          const next = { ...existing, ...patch, updatedAt: Date.now() };
          store.put(next);
          done(next);
        });
      });
    });
    if (merged === OWNER_MOVED) throw new StaleIdentityError();
    return merged;
  });
}

/**
 * Every key some remaining manifest records a pin on — every owner's, not just this one's.
 *
 * A removal releases one pin per key it took. When a manifest has no pin record to go by, the
 * caller falls back to the keys it would have taken, and this is what keeps that fallback from
 * releasing a pin it never held: a refcount belongs to whoever raised it, and guessing one down on
 * somebody else's behalf takes their downloaded track away from them. Read after the manifest is
 * deleted, so what comes back is exactly what is still being held.
 * @returns {Promise<Set<string>>}
 */
export async function readHeldPinnedKeys() {
  const db = await openLibrary();
  if (!db) return new Set();
  return runTransaction(db, COLLECTIONS_STORE, 'readonly', (stores, done) => {
    const request = stores[COLLECTIONS_STORE].getAll();
    request.onsuccess = () => {
      const held = new Set();
      for (const manifest of request.result || []) {
        for (const key of manifest?.pinnedKeys || []) held.add(key);
      }
      done(held);
    };
  });
}

/** Drop this owner's manifest. The caller unpins its keys first — this only forgets the intent. */
export async function deleteDownloadedCollection(collectionId) {
  if (!collectionId) return false;

  await whenOwnerReady();
  return serialize(async () => {
    const db = await openLibrary();
    if (!db) return false;
    await runTransaction(db, [COLLECTIONS_STORE, META_STORE], 'readwrite', (stores, done) => {
      const store = stores[COLLECTIONS_STORE];
      withOwnerKey(stores[META_STORE], (ownerKey) => {
        readVisibleCollection(store, ownerKey, collectionId, (existing) => {
          if (existing?.[COLLECTION_KEY_PATH]) store.delete(existing[COLLECTION_KEY_PATH]);
          done(undefined);
        });
      });
    });
    return true;
  });
}

// ── Hydrated releases ────────────────────────────────────────────────────────────────────────────

/** The key a stored release is filed under — entity, id and the version it was pinned at. */
/**
 * The kinds a release row is filed under. Declared here, beside the key builder, because the other
 * candidate home — the module that stores and restores the fallback pair — reaches account settings,
 * and a static import of that from the manifest builder closes a cycle back onto session auth.
 */
export const RELEASE_KIND = Object.freeze({ TRACK: 'track', ORBITER: 'orbiter', WORLD: 'world' });

export function releaseRecordKey({ kind, id, version }) {
  if (!kind || !id) return null;
  return `${kind}:${id}:version:${version ?? 'live'}`;
}

/**
 * Combine two observations of the same immutable release without letting a lean envelope erase the
 * hydrated fields an earlier download already stored. Default-pair capture often has only
 * `{ release }`; collection hydration can additionally carry ids and dependency releases.
 */
function mergeReleasePayload(existing, incoming) {
  if (!existing || typeof existing !== 'object') return incoming;
  if (!incoming || typeof incoming !== 'object') return existing;

  const merged = { ...existing, ...incoming };
  if (existing.release && incoming.release) {
    merged.release = { ...existing.release, ...incoming.release };
    if (existing.release.metadata && incoming.release.metadata) {
      merged.release.metadata = { ...existing.release.metadata, ...incoming.release.metadata };
    }
  }
  if (existing.hydration && incoming.hydration) {
    merged.hydration = { ...existing.hydration, ...incoming.hydration };
  }
  return merged;
}

/**
 * Keep a hydrated release payload so an offline boot has a session to build from. Stored by pinned
 * version, so the release a manifest names is the release that comes back — not whatever is newest.
 *
 * The viewer's own layer is taken OUT here and filed under them in `configurations`, because this
 * row has no owner in its key and two people downloading the same track version share it. See
 * "The personal layer inside a release" below. Both halves are written in ONE transaction: a
 * release stored with its effective settings missing and nowhere else to be found would boot at the
 * detected tempo with warp back on, which is the failure this whole seam exists to prevent.
 *
 * `expectedOwnerKey` carries who the caller started for, for the reason spelled out on
 * {@link writeDownloadedCollection}: these payloads were personalized for the person whose requests
 * fetched them, and a download that lands after somebody else has arrived must not file one
 * person's tempo, warp and loop under the other.
 * @param {{expectedOwnerKey?: string|null}} [options]
 * @returns {Promise<number>} how many release rows were written
 */
export async function writeStoredReleases(records, { expectedOwnerKey = null } = {}) {
  const byKey = new Map();
  for (const record of records || []) {
    if (!record?.key || !record?.payload) continue;
    const previous = byKey.get(record.key);
    byKey.set(record.key, previous
      ? { key: record.key, payload: mergeReleasePayload(previous.payload, record.payload) }
      : record);
  }
  const usable = [...byKey.values()];
  if (!usable.length) return 0;

  await whenOwnerReady();
  return serialize(async () => {
    const db = await openLibrary();
    if (!db) return 0;
    let wrote = 0;
    await runTransaction(
      db,
      [RELEASES_STORE, META_STORE, CONFIGURATIONS_STORE],
      'readwrite',
      (stores, done) => {
        withOwnerKey(stores[META_STORE], (ownerKey) => {
          if (expectedOwnerKey !== null && ownerKey !== expectedOwnerKey) {
            done(undefined);
            return;
          }
          wrote = usable.length;
          const storedAt = Date.now();
          for (const record of usable) {
            const effectiveSettings = readReleaseEffectiveSettings(record.payload);
            if (effectiveSettings) {
              stores[CONFIGURATIONS_STORE].put({
                ownerKey,
                kind: EFFECTIVE_SETTINGS_KIND,
                resourceKey: record.key,
                value: effectiveSettings,
                observedAt: storedAt,
              });
            }
            const incoming = withReleaseEffectiveSettings(
              withoutReleaseEffectiveSettings(record.payload),
              sharedEffectiveSettings(readReleaseEffectiveSettings(record.payload)),
            );
            const existing = stores[RELEASES_STORE].get(record.key);
            existing.onsuccess = () => {
              stores[RELEASES_STORE].put({
                key: record.key,
                // Keep the richest observation of an immutable release. A thin default-pair
                // envelope must not erase hydration a collection already stored under this key.
                payload: mergeReleasePayload(existing.result?.payload, incoming),
                storedAt,
              });
            };
          }
          done(undefined);
        });
      },
    );
    return wrote;
  });
}

/**
 * A stored release payload, put back together for the person who is actually here: the shared row
 * with everything personal taken off it, and THIS owner's own layer folded back on.
 *
 * The strip is not redundant with the one `writeStoredReleases` does, and leaving it out was a leak.
 * Rows written before the split still carry somebody's effective settings, and the one-shot repair
 * that lifts them out can fail — a quota, a transaction that aborts — leaving the row exactly as it
 * was for this boot. Trusting the row because no repair reported a problem is how the next person
 * to boot plays at the last person's tempo, warp and loop. So nothing PERSONAL reaches a reader
 * unless it was filed under the reader: the shared row is depersonalised on the way out, every time.
 *
 * Depersonalised, not emptied — see {@link sharedEffectiveSettings}. What a shared row is left
 * holding is what the track's OWNER set and what the analysis found, which is the same answer for
 * everybody who plays that release. An owner with no mirrored layer of their own gets exactly that:
 * a release resolving from the owner's settings and from detection, which is what somebody who
 * never chose anything is entitled to — rather than somebody else's choices presented as theirs.
 */
export async function readStoredRelease(key) {
  if (!key) return null;
  await whenOwnerReady();
  const db = await openLibrary();
  if (!db) return null;
  const row = await runTransaction(
    db,
    [RELEASES_STORE, META_STORE, CONFIGURATIONS_STORE],
    'readonly',
    (stores, done) => {
      const request = stores[RELEASES_STORE].get(key);
      request.onsuccess = () => {
        const release = request.result || null;
        if (!release) {
          done(null);
          return;
        }
        withOwnerKey(stores[META_STORE], (ownerKey) => {
          const mine = stores[CONFIGURATIONS_STORE].get([ownerKey, EFFECTIVE_SETTINGS_KIND, key]);
          mine.onsuccess = () => done({ ...release, effectiveSettings: mine.result?.value ?? null });
        });
      };
    },
  );
  if (!row?.payload) return null;
  // This owner's own mirrored layer is the whole answer where there is one: it was resolved for
  // them, owner and analysis values included. Where there is none, the shared row's own remainder
  // is served — everything in it that can be proven not to be one person's.
  return withReleaseEffectiveSettings(
    withoutReleaseEffectiveSettings(row.payload),
    row.effectiveSettings ?? sharedEffectiveSettings(readReleaseEffectiveSettings(row.payload)),
  );
}

/**
 * Forget release payloads no remaining manifest names.
 *
 * Run after a removal rather than alongside it: a release is shared exactly as bytes are, and
 * deleting one because ONE collection stopped wanting it would take it from the collection that
 * still does. Membership is recomputed from what is left, which is the only reading that cannot
 * drift out of step with the manifests themselves.
 *
 * "What is left" is every owner's manifests, not the current one's. A release is shared across
 * people on this browser exactly as the bytes are, and pruning against one person's list alone
 * would delete the payload the other person's downloaded collection boots from.
 */
export async function pruneStoredReleases() {
  return serialize(async () => {
    const db = await openLibrary();
    if (!db) return 0;

    return runTransaction(
      db,
      [COLLECTIONS_STORE, RELEASES_STORE, CONFIGURATIONS_STORE],
      'readwrite',
      (stores, done) => {
        const collectionsRequest = stores[COLLECTIONS_STORE].getAll();
        collectionsRequest.onsuccess = () => {
          const wanted = new Set();
          for (const manifest of collectionsRequest.result || []) {
            for (const key of manifest?.releaseKeys || []) wanted.add(key);
          }
          const releaseKeysRequest = stores[RELEASES_STORE].getAllKeys();
          releaseKeysRequest.onsuccess = () => {
            let dropped = 0;
            const orphaned = [];
            for (const key of releaseKeysRequest.result || []) {
              if (!wanted.has(key)) {
                stores[RELEASES_STORE].delete(key);
                orphaned.push(key);
                dropped += 1;
              }
            }
            if (!orphaned.length) {
              done(dropped);
              return;
            }
            // The personal layer goes with the release it belongs to — EVERY owner's, for the same
            // reason the release itself is pruned against every owner's manifests. A row left
            // behind here is a settings row for a release nobody can boot, kept forever.
            const orphanedKeys = new Set(orphaned);
            const configKeysRequest = stores[CONFIGURATIONS_STORE].getAllKeys();
            configKeysRequest.onsuccess = () => {
              for (const compound of configKeysRequest.result || []) {
                const [, kind, resourceKey] = compound || [];
                if (kind === EFFECTIVE_SETTINGS_KIND && orphanedKeys.has(resourceKey)) {
                  stores[CONFIGURATIONS_STORE].delete(compound);
                }
              }
              done(dropped);
            };
          };
        };
      },
    );
  });
}

// ── The personal layer inside a release ──────────────────────────────────────────────────────────

/**
 * A hydrated release is NOT the same thing for two people, and this is the seam where that stops
 * being invisible.
 *
 * Mycelium personalizes it as it builds it: the viewer's own track sync is folded together with the
 * owner's and with what the audio analysis found, and the resolved answer is written onto the
 * payload as `metadata.effectiveSettings`. That is the platform's own word for this layer — it is
 * what the backend writes and what the shared clock resolvers read — so it is the word used here
 * too, rather than a second name for one concept. Everything else in there — the assets, the
 * versions, the default orbiter and world — is the same for everybody.
 *
 * The `releases` store keys a payload by entity, id and version, with no owner in it, and says so
 * on purpose: bytes are shared, so their metadata is too. That was true of the release and false of
 * the effective settings riding inside it — two people downloading the same track version wrote
 * each other's tempo, warp, loop and downbeat into one row, and whoever booted next played at the
 * other person's settings.
 *
 * So the two are separated where they are stored: the release row keeps what is genuinely shared,
 * and the personal layer is filed in `configurations` under the owner it belongs to. The boot puts
 * them back together for the person who is actually here.
 */
const EFFECTIVE_SETTINGS_KIND = 'effective-settings';

/** Where mycelium writes the resolved layer, in both the enveloped and bare payload shapes. */
function effectiveSettingsHost(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const release = payload.release && typeof payload.release === 'object' ? payload.release : null;
  if (release?.metadata && typeof release.metadata === 'object') return release.metadata;
  if (payload.metadata && typeof payload.metadata === 'object') return payload.metadata;
  return null;
}

/** The viewer-specific layer a payload is carrying, or null when it carries none. */
export function readReleaseEffectiveSettings(payload) {
  const host = effectiveSettingsHost(payload);
  const value = host?.effectiveSettings;
  return value && typeof value === 'object' ? value : null;
}

/**
 * The same payload with the personal layer taken out — cloned along the path that changes, so the
 * caller's object is left alone. What comes back is what may be shared.
 */
export function withoutReleaseEffectiveSettings(payload) {
  if (!readReleaseEffectiveSettings(payload)) return payload;
  if (payload.release && typeof payload.release === 'object' && payload.release.metadata?.effectiveSettings) {
    const { effectiveSettings, ...metadata } = payload.release.metadata;
    return { ...payload, release: { ...payload.release, metadata } };
  }
  const { effectiveSettings, ...metadata } = payload.metadata;
  return { ...payload, metadata };
}

/**
 * The same payload with this viewer's layer put back. Cloned like the strip above. A null layer
 * returns the payload untouched: nothing mirrored is not the same as an empty answer, and inventing
 * an empty `effectiveSettings` would tell the resolvers a person chose the defaults.
 */
export function withReleaseEffectiveSettings(payload, effectiveSettings) {
  if (!effectiveSettings || typeof effectiveSettings !== 'object') return payload;
  if (!payload || typeof payload !== 'object') return payload;
  if (payload.release && typeof payload.release === 'object') {
    const metadata = { ...(payload.release.metadata || {}), effectiveSettings };
    return { ...payload, release: { ...payload.release, metadata } };
  }
  const metadata = { ...(payload.metadata || {}), effectiveSettings };
  return { ...payload, metadata };
}

/**
 * A resolved block is not uniformly personal, and treating it as though it were threw away data
 * that belongs to everybody.
 *
 * Every field in `sync` carries a `*Source` marker saying where its value came from: `user` is this
 * viewer's own choice, `owner` the track owner's canonical setting, `analysis` what the audio
 * analysis detected. Only the first is one person's. The other two are the same answer for
 * everybody who plays that release — shared exactly like the rest of the payload — and dropping
 * them buys no privacy at all: for a track nobody ever configured the block is entirely owner and
 * analysis, so emptying it cost a working offline boot and protected nothing.
 *
 * A field whose marker is MISSING is treated as personal and goes, and so is one whose marker says
 * something this vocabulary does not know. Neither can be PROVEN shared, and a payload minted
 * before the markers existed must not leak the tempo, warp and loop of whoever downloaded it to the
 * next person on the browser. Unattributable is personal; that is the property this seam exists to
 * keep. Only `sync` carries markers, so anything else the block holds is unattributable too.
 *
 * The loop REGION is a pair of fields under ONE marker, which is why the marker is derived per
 * field rather than looked up. That derivation is the same rule `SYNC_FIELD_MARKERS` in
 * `src/sync/personalTrackSync.js` spells out field by field; it is not imported from there because
 * that module now reads this store, and the two would import each other.
 *
 * @param {object|null} effectiveSettings a resolved block, as the payload carries it
 * @returns {object|null} the shared remainder — the block ITSELF when nothing in it was personal,
 *   so a caller can tell by identity that there is nothing to rewrite, and null when nothing
 *   survives, because an empty block would tell the resolvers a person chose the defaults
 */
export function sharedEffectiveSettings(effectiveSettings) {
  const sync = effectiveSettings?.sync;
  if (!sync || typeof sync !== 'object') return null;

  const shared = {};
  for (const [field, value] of Object.entries(sync)) {
    if (field.endsWith('Source')) continue;
    const marker = field === 'loopStartSec' || field === 'loopEndSec'
      ? 'loopRangeSource'
      : `${field}Source`;
    if (sync[marker] !== 'owner' && sync[marker] !== 'analysis') continue;
    shared[field] = value;
    shared[marker] = sync[marker];
  }

  const kept = Object.keys(shared).length;
  if (!kept) return null;
  return kept === Object.keys(sync).length && Object.keys(effectiveSettings).length === 1
    ? effectiveSettings
    : { sync: shared };
}

/** The kind name the personal layer is filed under — the registry's, and the boot's, one word for it. */
export const RELEASE_EFFECTIVE_SETTINGS_KIND = EFFECTIVE_SETTINGS_KIND;


// ── User configuration ───────────────────────────────────────────────────────────────────────────

/**
 * The user's own configuration, mirrored so it survives a boot with no network.
 *
 * ## The key has the owner in it, and that is not decoration
 *
 * A row is filed under `[ownerKey, kind, resourceKey]`. Putting the uid INSIDE the row instead —
 * filed under `midi:orbiter:<id>` and filtered on read — does not prevent the collision, it only
 * hides it: the second person's write still lands on the first person's key and the first person's
 * settings are gone. The owner has to be part of what makes the row unique, not part of what is
 * stored in it. This is the same lesson `collections` learned at v2, applied before rather than
 * after somebody lost something.
 *
 * `anon` is a real owner here for the same reason it is there: a signed-out visitor's configuration
 * is theirs, and the next account to sign in does not inherit it.
 *
 * ## Every read waits for the owner
 *
 * `whenOwnerReady()`, exactly as `readStoredRelease` does. Without it a read issued before the
 * reconcile answers against whoever was here last — which for configuration means applying the
 * previous person's MIDI mappings and the previous person's tempo to this person's session.
 *
 * ## `observedAt`, not `updatedAt`
 *
 * The browser can honestly say WHEN IT SAW a value. It cannot say which of two values is newer:
 * these endpoints carry no revision and no ETag, so a timestamp minted here would be a
 * reconciliation field that cannot reconcile. It is recorded for staleness reporting and for a
 * later `?since=` read, and nothing resolves a conflict with it.
 *
 * ## One row per entry
 *
 * Learning one CC rewrites one slice's row, not the whole configuration. The row shape IS the entry
 * shape of the pack envelope, so an export later is a walk over rows rather than a second format.
 */

/**
 * Whose library this browser is answering for, right now — manifests, releases and configuration
 * alike, since one meta row settles all three.
 *
 * A caller that reads this before starting a request and hands it back when the answer lands can
 * refuse to file that answer under somebody else — which is the whole failure mode when identity
 * changes mid-flight. Every write that derives an owner takes it as `expectedOwnerKey`.
 * @returns {Promise<string|null>}
 */
export async function readConfigurationOwnerKey() {
  await whenOwnerReady();
  const db = await openLibrary();
  if (!db) return null;
  return runTransaction(db, META_STORE, 'readonly', (stores, done) => {
    withOwnerKey(stores[META_STORE], (ownerKey) => done(ownerKey));
  });
}

/**
 * One mirrored value, or null when this owner has none.
 * @param {string} kind
 * @param {string} resourceKey
 * @returns {Promise<{value: any, observedAt: number}|null>}
 */
export async function readUserConfiguration(kind, resourceKey) {
  if (!kind || !resourceKey) return null;
  await whenOwnerReady();
  const db = await openLibrary();
  if (!db) return null;
  return runTransaction(db, [CONFIGURATIONS_STORE, META_STORE], 'readonly', (stores, done) => {
    withOwnerKey(stores[META_STORE], (ownerKey) => {
      const request = stores[CONFIGURATIONS_STORE].get([ownerKey, kind, resourceKey]);
      request.onsuccess = () => {
        const row = request.result;
        done(row ? { value: row.value, observedAt: Number(row.observedAt) || 0 } : null);
      };
    });
  });
}

/**
 * Mirror values for whoever the owner row names. The owner is stamped here rather than passed in,
 * so there is one answer to "whose configuration is this" and it is the one the reads filter on.
 *
 * `expectedOwnerKey` is how a caller says who it STARTED for. A request issued as one person and
 * landing after somebody else has arrived would otherwise be filed under the person who arrived —
 * the first account's mappings written into the second account's rows, by a race with no gesture
 * behind it. Given one, a write for the wrong owner is dropped rather than misfiled.
 *
 * @param {Array<{kind: string, resourceKey: string, value: any}>} entries
 * @param {{expectedOwnerKey?: string|null}} [options]
 * @returns {Promise<number>} how many rows were written
 */
export async function writeUserConfigurations(entries, { expectedOwnerKey = null } = {}) {
  const usable = (entries || []).filter(
    (entry) => entry?.kind && entry?.resourceKey && entry.value !== undefined,
  );
  if (!usable.length) return 0;

  await whenOwnerReady();
  return serialize(async () => {
    const db = await openLibrary();
    if (!db) return 0;
    let wrote = 0;
    await runTransaction(db, [CONFIGURATIONS_STORE, META_STORE], 'readwrite', (stores, done) => {
      withOwnerKey(stores[META_STORE], (ownerKey) => {
        if (expectedOwnerKey !== null && ownerKey !== expectedOwnerKey) {
          done(undefined);
          return;
        }
        wrote = usable.length;
        const observedAt = Date.now();
        for (const entry of usable) {
          stores[CONFIGURATIONS_STORE].put({
            ownerKey,
            kind: entry.kind,
            resourceKey: entry.resourceKey,
            value: entry.value,
            observedAt,
          });
        }
        done(undefined);
      });
    });
    return wrote;
  });
}

/**
 * Forget one mirrored value. Used when the server says a resource is genuinely GONE — an absence
 * the server states is a fact worth mirroring, and leaving the old row would resurrect a mapping
 * somebody deleted on every later offline boot.
 */
export async function deleteUserConfiguration(kind, resourceKey, { expectedOwnerKey = null } = {}) {
  if (!kind || !resourceKey) return false;
  await whenOwnerReady();
  return serialize(async () => {
    const db = await openLibrary();
    if (!db) return false;
    let deleted = false;
    await runTransaction(db, [CONFIGURATIONS_STORE, META_STORE], 'readwrite', (stores, done) => {
      withOwnerKey(stores[META_STORE], (ownerKey) => {
        // Same backstop the write has, for the same reason: an answer about the person who has
        // left, arriving after somebody else has, must not delete the arriving person's row. The
        // caller's epoch catches this first — but only until the moment this call blocks on the
        // owner gate, and a reconcile landing inside that window is exactly the case at hand.
        if (expectedOwnerKey !== null && ownerKey !== expectedOwnerKey) {
          done(undefined);
          return;
        }
        deleted = true;
        stores[CONFIGURATIONS_STORE].delete([ownerKey, kind, resourceKey]);
        done(undefined);
      });
    });
    return deleted;
  });
}

// ── Carrying the old rows across ─────────────────────────────────────────────────────────────────

/** Which one-shot repairs this database has already had. Kept beside the owner, in `meta`. */
const REPAIRS_ROW_KEY = 'repairs';
/** The stored name of the repair below. Written in people's browsers already — it does not change. */
const EFFECTIVE_SETTINGS_REPAIR = 'personalisationMoved';

/**
 * Take the PERSONAL half out of the release rows that predate the split, and keep it for nobody.
 *
 * Nothing left in the database can say whose settings a pre-split row holds. The obvious guess is
 * the manifests — a manifest names the release keys its download stored, and a manifest has an
 * owner, so a key claimed by exactly one owner looks like that owner's. It is not evidence. Current
 * ownership cannot prove who WROTE the row: two people download the same track version, the second
 * one's settings win the shared row, then the second person removes their collection. One manifest
 * is left, it belongs to the first person, and the row it would be handed holds the second person's
 * tempo, warp and loop. That is the exact leak this split exists to end, arriving through the
 * repair meant to close it.
 *
 * So no guess is made at all — about the personal half. Dropping the WHOLE block was the first
 * answer here and it was too blunt: a resolved block also carries what the track's owner set and
 * what the analysis detected, which is the same for every listener and is not anybody's to leak.
 * For a track this person never configured the block is nothing BUT that, and throwing it away cost
 * an offline boot its tempo and downbeat to protect nothing. So the walk removes what is marked as
 * one person's — and what carries no marker at all, which cannot be proven shared — and leaves the
 * rest where it is. See {@link sharedEffectiveSettings} for the rule.
 *
 * The serving path does not depend on this having run — `readStoredRelease` applies the same rule
 * on every read — so a repair that fails costs nothing but the disk it did not tidy.
 *
 * Runs AFTER the upgrade, not inside it, and records that it ran. A repair that fails leaves a
 * database that still works; an abandoned versionchange transaction leaves one that never will.
 */
async function stripStoredEffectiveSettings(db) {
  const done = await runTransaction(db, META_STORE, 'readonly', (stores, settle) => {
    const request = stores[META_STORE].get(REPAIRS_ROW_KEY);
    request.onsuccess = () => settle(request.result?.[EFFECTIVE_SETTINGS_REPAIR] === true);
  })
    // A meta read that failed says nothing about whether the repair has run, and the two ways of
    // being wrong are not equal. Assuming it HAS run skips the walk for the whole reconcile on a
    // transient failure, and leaves one person's settings sitting in a shared row. Assuming it has
    // NOT costs a walk over the release rows that finds nothing to strip and marks itself done. So
    // the cheap mistake is the default: on no answer, run it.
    .catch(() => false);
  if (done) return;

  await runTransaction(db, [RELEASES_STORE, META_STORE], 'readwrite', (stores, settle) => {
    const releases = stores[RELEASES_STORE].getAll();
    releases.onsuccess = () => {
      for (const row of releases.result || []) {
        const carried = readReleaseEffectiveSettings(row?.payload);
        if (!carried) continue;
        const shared = sharedEffectiveSettings(carried);
        // The same block back means there was nothing personal in it. Rewriting the row anyway
        // would be a put per release on every reconcile that has to repeat this walk.
        if (shared === carried) continue;
        stores[RELEASES_STORE].put({
          ...row,
          payload: withReleaseEffectiveSettings(withoutReleaseEffectiveSettings(row.payload), shared),
        });
      }
      stores[META_STORE].put({ key: REPAIRS_ROW_KEY, [EFFECTIVE_SETTINGS_REPAIR]: true });
      settle(undefined);
    };
  }).catch((error) => {
    // Left unmarked on purpose: the next open tries again. Nothing is served in the meantime that
    // would not have been served anyway — the read path strips the shared row regardless.
    console.warn('[offline] Old effective settings could not be cleared out of the release rows yet.', error);
  });
}

// ── Owner ────────────────────────────────────────────────────────────────────────────────────────

async function emptyLibrary(db) {
  const names = [COLLECTIONS_STORE, RELEASES_STORE, META_STORE, CONFIGURATIONS_STORE];
  await runTransaction(db, names, 'readwrite', (stores, done) => {
    for (const name of names) stores[name].clear();
    done(undefined);
  });
}

/**
 * Empty the library outright — every owner's manifests, every stored release, the owner row too.
 *
 * Nothing in the sign-in or sign-out path calls this, and that is deliberate: a download stays
 * until the person who made it removes it. It exists for the case where the library as a whole is
 * being thrown away. The caller is responsible for the bytes.
 */
export async function clearOfflineLibrary() {
  return serialize(async () => {
    const db = await openLibrary();
    if (!db) return;
    await emptyLibrary(db);
  });
}

/**
 * Give the manifests that predate owners the owner they have been standing for.
 *
 * Those manifests are visible to everyone until this runs, which is the honest reading of no
 * evidence — but the owner row IS evidence: it names the person this browser's library belonged to
 * when they were stored. So the first reconcile that knows an owner writes it onto them, and from
 * then on they behave like any other download.
 *
 * Runs whether or not the arriving identity could be established, because it does not decide who
 * owns anything — it only writes down what the owner row already implied.
 *
 * Finding NO owner row is itself a fact, and it is recorded: those manifests are marked as having
 * been met with no evidence, and are never stamped afterwards. Without the mark, the owner row this
 * very reconcile is about to write would be read by the NEXT one as though it had been standing for
 * them all along — so the first person to sign in would silently acquire downloads that predate
 * them, and the second person would stop seeing what they were entitled to see.
 * @returns {Promise<number>} how many manifests were stamped
 */
async function stampUnownedCollections(db) {
  return runTransaction(db, [COLLECTIONS_STORE, META_STORE], 'readwrite', (stores, done) => {
    const metaRequest = stores[META_STORE].get(OWNER_ROW_KEY);
    metaRequest.onsuccess = () => {
      const row = metaRequest.result;
      const store = stores[COLLECTIONS_STORE];
      const all = store.getAll();
      all.onsuccess = () => {
        const unowned = (all.result || []).filter(
          (manifest) => manifest?.collectionId && ownerKeyOfManifest(manifest) === OWNER_UNOWNED,
        );

        // No owner row: nothing has ever been established here, so there is nobody to stamp these
        // as — and nobody there ever will be. Written down so a later reconcile does not mistake
        // the row it is about to create for evidence about the past.
        if (!row) {
          for (const manifest of unowned) {
            if (manifest.ownerless) continue;
            store.put({ ...manifest, ownerless: true });
          }
          done(0);
          return;
        }

        const ownerUid = row.uid ?? null;
        const ownerKey = ownerKeyForUid(ownerUid);
        let stamped = 0;
        for (const manifest of unowned) {
          // Met once with no evidence; it stays everybody's.
          if (manifest.ownerless) continue;
          store.delete(manifest[COLLECTION_KEY_PATH]);
          store.put({
            ...manifest,
            ownerUid,
            [COLLECTION_KEY_PATH]: collectionRecordId(ownerKey, manifest.collectionId),
          });
          stamped += 1;
        }
        done(stamped);
      };
    };
  });
}

/**
 * Settle whose library this browser is showing. Removes nothing, ever.
 *
 * Ownership is written on the manifest, so a change of identity is a change of what is VISIBLE and
 * nothing else. Two people sharing a browser each keep their own downloads, and each sees only
 * their own; the bytes underneath are shared, refcounted, and outlive either of them signing out.
 * Anonymous is an identity here in both directions — a signed-out visitor's downloads are theirs,
 * and the next account to sign in does not inherit them.
 *
 * The owner row is still written, and is still this store's own rather than trusted from the audio
 * ledger: it is what an offline boot — where nobody can be asked — reads to decide which library to
 * show. Which is also why an unestablished identity leaves it exactly as it was: silence is not
 * somebody else arriving, and overwriting the row on silence would hide the library of the very
 * person who downloaded it, on the first boot where having it mattered.
 *
 * @param {string|null} uid the viewer auth resolved, or null when nobody is signed in
 * @returns {Promise<boolean>} whether the established identity differs from the row it replaced
 */
export async function reconcileOfflineLibraryOwner(uid, { identityEstablished = true } = {}) {
  const viewerUid = uid || null;

  return serialize(async () => {
    const db = await openLibrary();
    if (!db) return false;

    await stampUnownedCollections(db);
    // Housekeeping rather than a correctness step: it takes the personal half out of the settings
    // pre-split rows are still carrying, which nothing may be served anyway. Done here because a
    // reconcile is the one moment that already has the database open and is allowed to be slow.
    await stripStoredEffectiveSettings(db);
    if (!identityEstablished) return false;

    const owner = await runTransaction(db, META_STORE, 'readonly', (stores, done) => {
      const request = stores[META_STORE].get(OWNER_ROW_KEY);
      request.onsuccess = () => {
        const row = request.result;
        done({ known: Boolean(row), uid: row?.uid || null });
      };
    });

    await runTransaction(db, META_STORE, 'readwrite', (stores, done) => {
      stores[META_STORE].put({ key: OWNER_ROW_KEY, uid: viewerUid });
      done(undefined);
    });

    return owner.known && owner.uid !== viewerUid;
  }).finally(() => {
    // Released whatever the outcome: a reconcile that failed must not leave every later read
    // waiting forever on a gate nothing will open.
    markOwnerReady();
  });
}
